// ============================================================
// Staff Management React Query Hooks
// ============================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSchool } from './useSchool';
import { staffManagementService } from '@/services/staffManagementService';
import { roleAccessService } from '@/services/superAdmin/roleAccessService';

// ── School Roles Hook ──────────────────────────────────────────
export function useSchoolRoles() {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['schoolRoles', schoolId],
    queryFn: () => roleAccessService.getSchoolRoles(schoolId),
    enabled: !!schoolId,
    staleTime: 1000 * 60 * 5,
  });
}

// ── Department Hooks ──────────────────────────────────────────
export function useDepartments(params = {}) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['departments', schoolId, params],
    queryFn: () => staffManagementService.getDepartments(schoolId, params),
    enabled: !!schoolId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useCreateDepartment() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => staffManagementService.createDepartment(schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments', schoolId] });
    },
  });
}

export function useUpdateDepartment() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) => staffManagementService.updateDepartment(id, schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments', schoolId] });
    },
  });
}

export function useDeleteDepartment() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => staffManagementService.deleteDepartment(id, schoolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments', schoolId] });
    },
  });
}

// ── Designation Hooks ─────────────────────────────────────────
export function useDesignations(params = {}) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['designations', schoolId, params],
    queryFn: () => staffManagementService.getDesignations(schoolId, params),
    enabled: !!schoolId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateDesignation() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => staffManagementService.createDesignation(schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designations', schoolId] });
    },
  });
}

export function useUpdateDesignation() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) => staffManagementService.updateDesignation(id, schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designations', schoolId] });
    },
  });
}

export function useDeleteDesignation() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => staffManagementService.deleteDesignation(id, schoolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designations', schoolId] });
    },
  });
}

// ── Staff Hooks ───────────────────────────────────────────────
export function useStaffList(params = {}) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['staff', schoolId, params],
    queryFn: () => staffManagementService.getStaffList(schoolId, params),
    enabled: !!schoolId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useStaffById(id) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['staff', schoolId, id],
    queryFn: () => staffManagementService.getStaffById(id, schoolId),
    enabled: !!schoolId && !!id,
  });
}

export function useStaffProfile(id) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['staffProfile', schoolId, id],
    queryFn: () => staffManagementService.getStaffProfile(id, schoolId),
    enabled: !!schoolId && !!id,
  });
}

export function useCreateStaff() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => staffManagementService.createStaff(schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', schoolId] });
    },
  });
}

export function useUpdateStaff() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) => staffManagementService.updateStaff(id, schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', schoolId] });
    },
  });
}

export function useUpdateStaffStatus() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, employmentStatus }) =>
      staffManagementService.updateStaffStatus(id, schoolId, employmentStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', schoolId] });
    },
  });
}

export function useDeleteStaff() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => staffManagementService.deleteStaff(id, schoolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', schoolId] });
    },
  });
}

export function useStaffDocuments(staffId) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['staffDocuments', schoolId, staffId],
    queryFn: () => staffManagementService.getStaffDocuments(staffId, schoolId),
    enabled: !!schoolId && !!staffId,
  });
}

export function useStaffDocumentById(documentId, staffId) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['staffDocument', schoolId, staffId, documentId],
    queryFn: () => staffManagementService.getStaffDocumentById(documentId, staffId, schoolId),
    enabled: !!schoolId && !!staffId && !!documentId,
  });
}

export function useCreateStaffDocument() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ staffId, ...documentData }) =>
      staffManagementService.createStaffDocument(staffId, schoolId, documentData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['staffDocuments', schoolId, variables.staffId] });
      queryClient.invalidateQueries({ queryKey: ['staffProfile', schoolId, variables.staffId] });
      queryClient.invalidateQueries({ queryKey: ['staff', schoolId] });
    },
  });
}

export function useUpdateStaffDocument() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, staffId, ...documentData }) =>
      staffManagementService.updateStaffDocument(documentId, staffId, schoolId, documentData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['staffDocuments', schoolId, variables.staffId] });
      queryClient.invalidateQueries({ queryKey: ['staffProfile', schoolId, variables.staffId] });
      queryClient.invalidateQueries({ queryKey: ['staff', schoolId] });
    },
  });
}

export function useDeleteStaffDocument() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, staffId }) =>
      staffManagementService.deleteStaffDocument(documentId, staffId, schoolId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['staffDocuments', schoolId, variables.staffId] });
      queryClient.invalidateQueries({ queryKey: ['staffProfile', schoolId, variables.staffId] });
      queryClient.invalidateQueries({ queryKey: ['staff', schoolId] });
    },
  });
}

// ── Leave Type Hooks ──────────────────────────────────────────
export function useLeaveTypes(params = {}) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['leaveTypes', schoolId, params],
    queryFn: () => staffManagementService.getLeaveTypes(schoolId, params),
    enabled: !!schoolId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateLeaveType() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => staffManagementService.createLeaveType(schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveTypes', schoolId] });
    },
  });
}

export function useUpdateLeaveType() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) => staffManagementService.updateLeaveType(id, schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveTypes', schoolId] });
    },
  });
}

export function useDeleteLeaveType() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => staffManagementService.deleteLeaveType(id, schoolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveTypes', schoolId] });
    },
  });
}

// ── Staff Discipline Hooks ─────────────────────────────────────
export function useStaffDisciplineStatusEnums() {
  return useQuery({
    queryKey: ['staffDisciplineEnums'],
    queryFn: () => staffManagementService.getDisciplineStatusEnums(),
    staleTime: 1000 * 60 * 30, // 30 minutes static enums
  });
}

export function useStaffDisciplineHistory(staffId) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['staffDisciplineHistory', schoolId, staffId],
    queryFn: () => staffManagementService.getStaffDisciplineHistory(staffId, schoolId),
    enabled: !!schoolId && !!staffId,
  });
}

export function useUpdateStaffDiscipline() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ staffId, status, remarks }) =>
      staffManagementService.updateStaffDiscipline(staffId, schoolId, { status, remarks }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['staffDisciplineHistory', schoolId, variables.staffId] });
      queryClient.invalidateQueries({ queryKey: ['staff', schoolId] });
    },
  });
}

